import React, { useState, useEffect } from 'react';
import { Image, message } from 'antd';
import { useParams } from 'react-router-dom';
import isDownload from 'hooks/isDownload';
import { IStaticImage, IProject } from 'utils/types/storyTypes';
import SearchInput from 'components/search/SearchInput';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import BackgroundRegisterModal from './BackgroundRegisterModal';
import BackgroundUpdateModal from './BackgroundUpdateModal';
import NoImage from 'components/etc/NoImage';

import DefaultButton from 'components/button/DefaultButton';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { backgroundApi } from 'apis/resource/backgroundApis';
import ActionBtn from './ActionBtn';
import DeleteModal from 'components/modal/DeleteModal';
import { handleMessage } from 'utils/helper/message';
/**
 * * 리소스 - 배경 목록
 */

interface IProps {
  selectProject: IProject;
}

const BackgroundTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string>(''); // 검색어
  const [visible, setVisible] = useState({
    register: false,
    update: false,
    delete: false,
    zip: false,
  }); // 모달 show on/off

  // 선택한 배경 (모달에서 사용)
  const [selectedBackground, setSelectedBackground] = useState<IStaticImage>();

  const [refresh, setRefresh] = useState<boolean>(false);

  const [originData, setOriginData] = useState<IStaticImage[]>([]); // 조회 데이터
  const [tableData, setTableData] = useState<IStaticImage[]>([]); // 테이블의 source

  const handleFetchData = async () => {
    if (projectId) {
      try {
        // const resBgData = await backgroundApi.backgroundList(projectId);

        const list: IStaticImage[] = await backgroundApi.backgroundList(
          projectId,
        );
        console.log(`handleFetchData : `, list);

        // setBackgroundData(resBgData.list);
        setOriginData(list);
        setTableData(list);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // handle search input
  useEffect(() => {
    if (searchData && searchData.length) {
      setTableData(
        tableData.filter((item) =>
          item.image_name.toLowerCase().includes(searchData.toLowerCase()),
        ),
      );
    } else {
      setTableData(originData);
    }
  }, [searchData]);

  /// 다운로드
  const handleDownload = (record) => {
    // 다운로드 함수로 보내준다.
    isDownload(record);
  };

  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // 배경 삭제
  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await backgroundApi.backgroundDelete(selectedBackground?.id);
      setRefresh(!refresh);
      setDeleteLoading(false);
      setVisible({ ...visible, delete: false });
    } catch (err) {
      setDeleteLoading(false);
      handleMessage(message, 'error', 'Please try again');
      console.log('Delete background error:', err);
    }
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Preview',
      dataIndex: 'image_url',
      width: '10rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage />
          ) : (
            <Image
              src={_}
              alt={record.image_name}
              height={80}
              width={94}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.backgroundContent.tableContent.backgroundName',
      ),
      dataIndex: 'image_name',
      width: '20rem',
      sorter: (a, b) => {
        if (a.image_name && b.image_name) {
          return a.image_name.localeCompare(b.image_name, undefined, {
            sensitivity: 'base',
          });
        } else if (a.image_name && !b.image_name) {
          return -1;
        } else if (!a.image_name && b.image_name) {
          return 1;
        } else {
          return 0;
        }
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.backgroundContent.tableContent.gameScale',
      ),
      dataIndex: 'game_scale',
      width: '10rem',
    },
    {
      title: '',
      dataIndex: '',
      width: '10rem',
      render: (text, record) => (
        <ActionWrapper>
          <ActionBtn
            handleClickDownload={handleDownload}
            record={record}
            selectProject={selectProject}
            setVisible={setVisible}
            visible={visible}
            setSelectedBackground={setSelectedBackground}
            selectedBackground={selectedBackground}
          />
        </ActionWrapper>
      ),
    },
  ];

  return (
    <>
      <TableToolbar>
        <SearchInput
          placeholder={t(
            'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.backgroundContent.header.searchInputPlaceholder',
          )}
          setSearchData={setSearchData}
        />
        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, register: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.backgroundContent.header.registerBackground',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <MainTable columns={columnsOrigin} dataSource={tableData} />

      {/* 모달 모음 */}
      <BackgroundRegisterModal
        visible={visible.register}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
      />

      <BackgroundUpdateModal
        visible={visible.update}
        setVisible={setVisible}
        selectedData={selectedBackground}
        setSelectedData={setSelectedBackground}
        setRefresh={setRefresh}
        refresh={refresh}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => setVisible({ ...visible, delete: e })}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.backgroundContent.tableContent.deleteMessage',
        )}
        label={selectedBackground?.image_name}
        handleDelete={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default BackgroundTable;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 11px;
  p {
    font-weight: 450 !important;
  }
  img {
    cursor: pointer;
  }
`;
const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
